// src/pages/ClientEdit.js - VERSION AVEC GESTION DES ERREURS D'ABONNEMENT
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import countryCodes from "../data/countryCodes"; // Import des codes pays

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const ClientEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    nom_client: "",
    prenom_client: "",
    tel_client: "",
    email_client: "",
    type_client: "1", // 1=Entreprise, 2=Particulier
    date_debut_contrat_client: "",
    date_fin_contrat_client: "",
    piece_identite_client: "",
    nif_client: "",
    rccm_client: "",
    statut_client: "1" // 1=Actif, 0=Inactif
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // États pour le sélecteur d'indicatif téléphonique
  const [showCountryList, setShowCountryList] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countryCodes?.find(c => c.code === "+227") || countryCodes?.[0] || { 
      code: "+227", 
      name: "Niger", 
      flag: "🇳🇪",
      phoneLength: 8
    }
  );
  const [searchCountry, setSearchCountry] = useState("");
  const countryListRef = useRef(null);

  // Références pour fermer le dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryListRef.current && !countryListRef.current.contains(event.target)) {
        setShowCountryList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  /* ====== FONCTIONS POUR LE TÉLÉPHONE ====== */
  
  // Fonction pour extraire les chiffres
  const extractPhoneDigits = (phoneValue) => {
    if (!phoneValue) return "";
    // Si le numéro commence par un indicatif, on le supprime d'abord
    let digits = phoneValue;
    const countryCode = selectedCountry?.code;
    if (countryCode && phoneValue.startsWith(countryCode)) {
      digits = phoneValue.substring(countryCode.length);
    }
    return digits.replace(/\D/g, "");
  };

  // Fonction pour formater avec indicatif
  const formatFullPhoneNumber = (phoneDigits, countryCode = "+227") => {
    if (!phoneDigits) return "";
    return countryCode + phoneDigits;
  };

  // Fonction pour générer exemple
  const generatePhoneExample = (phoneLength) => {
    if (!phoneLength) return "";
    let example = "";
    for (let i = 0; i < phoneLength; i++) {
      example += (i % 10).toString();
    }
    return example;
  };

  // Fonction pour détecter l'indicatif d'un numéro
  const detectCountryCode = (phoneNumber) => {
    if (!phoneNumber) return null;
    
    // Chercher le pays correspondant à l'indicatif
    const foundCountry = countryCodes.find(country => 
      phoneNumber.startsWith(country.code)
    );
    
    return foundCountry || countryCodes[0];
  };

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
        
        // Vérifier si l'utilisateur est gestionnaire
        if (String(normalizedData.profile).trim() !== "3") {
          setError("Accès refusé. Seuls les gestionnaires peuvent modifier les clients.");
          setLoading(false);
        }
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  /* ====== COMPOSANT COUNTRYSELECTOR ====== */
  const CountrySelector = ({ 
    showList, setShowList, selectedCountry, setSelectedCountry, 
    search, setSearch, listRef, disabled 
  }) => {
    // Filtrer les pays de manière SÉCURISÉE
    const filteredCountries = (countryCodes || []).filter(country => {
      if (!country) return false;
      
      const searchTerm = (search || "").toLowerCase();
      const countryName = String(country.name || country.label || "").toLowerCase();
      const countryCode = String(country.code || "").toLowerCase();
      
      return countryName.includes(searchTerm) || countryCode.includes(searchTerm);
    });

    return (
      <div ref={listRef} className="position-relative">
        <button
          type="button"
          className="btn d-flex align-items-center justify-content-between"
          onClick={() => setShowList(!showList)}
          disabled={disabled}
          style={{
            backgroundColor: '#F5F7FA',
            border: `1px solid #e0e0e0`,
            borderRadius: '8px',
            padding: '8px 12px',
            width: '120px',
            minHeight: '44px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
        >
          <span className="me-2" style={{ fontSize: '1.2rem' }}>{selectedCountry?.flag || "🏳️"}</span>
          <span style={{ fontSize: '0.9rem', color: '#2E2E2E', fontWeight: '500' }}>
            {selectedCountry?.code || "+227"}
          </span>
          <i className="bi bi-chevron-down ms-2" style={{ fontSize: '0.8rem', color: '#666' }}></i>
        </button>

        {showList && !disabled && (
          <div style={{
            position: 'absolute', top: '100%', left: '0', width: '280px', maxHeight: '300px',
            backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, overflow: 'hidden', marginTop: '4px'
          }}>
            <div className="p-2 border-bottom">
              <div className="input-group">
                <span className="input-group-text" style={{ 
                  backgroundColor: '#F5F7FA', borderColor: '#e0e0e0', padding: '8px 12px'
                }}>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un pays..."
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ fontSize: '0.9rem', borderColor: '#e0e0e0', padding: '8px 12px' }}
                  autoFocus
                />
              </div>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredCountries.map((country, index) => {
                if (!country) return null;
                
                return (
                  <button
                    key={index}
                    type="button"
                    className="btn btn-light text-start w-100 border-0 rounded-0"
                    onClick={() => {
                      if (country) {
                        setSelectedCountry(country);
                        setShowList(false);
                        setSearch("");
                      }
                    }}
                    style={{ 
                      padding: '10px 16px', 
                      borderBottom: '1px solid #f0f0f0', 
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <div className="d-flex align-items-center">
                      <span className="me-3" style={{ fontSize: '1.2rem', minWidth: '30px' }}>
                        {country?.flag || "🏳️"}
                      </span>
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: '500' }}>
                          {country?.name || country?.label || "Pays"}
                        </div>
                        <small style={{ color: '#666' }}>{country?.code || ""}</small>
                        {country?.phoneLength && (
                          <small style={{ color: '#666', display: 'block' }}>
                            {country.phoneLength} chiffres
                          </small>
                        )}
                      </div>
                      {selectedCountry?.code === country?.code && <i className="bi bi-check text-primary"></i>}
                    </div>
                  </button>
                );
              })}
              
              {filteredCountries.length === 0 && (
                <div className="text-center py-3 text-muted" style={{ fontSize: '0.9rem' }}>
                  Aucun pays trouvé
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Charger les données du client à modifier
  useEffect(() => {
    const fetchClientData = async () => {
      if (!userInfo || !id) return;
      
      setLoading(true);
      setError("");
      setValidationErrors([]);
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/clients/get.php?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data?.success && response.data.client) {
          const client = response.data.client;
          
          // Stocker les données originales
          setOriginalData(client);
          
          // Détecter le pays à partir du numéro de téléphone
          let detectedCountry = countryCodes[0]; // Par défaut Niger
          if (client.tel_client) {
            detectedCountry = detectCountryCode(client.tel_client);
          }
          
          // Mettre à jour le pays sélectionné
          setSelectedCountry(detectedCountry);
          
          // Extraire les chiffres du numéro de téléphone
          const phoneDigits = extractPhoneDigits(client.tel_client || "");
          
          // Préparer les données pour le formulaire
          const formattedData = {
            nom_client: client.nom_client || "",
            prenom_client: client.prenom_client || "",
            tel_client: phoneDigits, // Stocker uniquement les chiffres
            email_client: client.email_client || "",
            type_client: String(client.type_client || "1"),
            date_debut_contrat_client: client.date_debut_contrat_client 
              ? client.date_debut_contrat_client.split(" ")[0]
              : "",
            date_fin_contrat_client: client.date_fin_contrat_client
              ? client.date_fin_contrat_client.split(" ")[0]
              : "",
            piece_identite_client: client.piece_identite_client || "",
            nif_client: client.nif_client || "",
            rccm_client: client.rccm_client || "",
            statut_client: String(client.statut_client || "1")
          };
          
          setFormData(formattedData);
          
          console.log("Données chargées:", {
            originalPhone: client.tel_client,
            detectedCountry: detectedCountry,
            phoneDigits: phoneDigits
          });
        } else {
          setError(response.data?.message || "Client non trouvé");
        }
      } catch (err) {
        console.error("Erreur:", err);
        
        if (err.response?.status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          localStorage.clear();
          setTimeout(() => navigate("/login"), 2000);
        } else if (err.response?.status === 403) {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        } else if (err.response?.status === 404) {
          setError("Client non trouvé.");
          setTimeout(() => navigate("/clients"), 3000);
        } else if (!err.response) {
          setError("Serveur indisponible.");
        } else {
          setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchClientData();
  }, [id, userInfo, navigate]);

  // Gestion des changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Pour le champ téléphone, n'accepter que les chiffres
    if (name === "tel_client") {
      const maxLength = selectedCountry?.phoneLength || 15;
      const digitsOnly = value.replace(/\D/g, "");
      const limitedDigits = digitsOnly.substring(0, maxLength);
      
      setFormData(prev => ({ ...prev, [name]: limitedDigits }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Effacer les erreurs de validation
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
    if (error) {
      setError("");
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = [];
    
    if (!formData.nom_client.trim()) {
      errors.push("Le nom est requis");
    }
    
    if (!formData.tel_client.trim()) {
      errors.push("Le téléphone est requis");
    } else {
      // Validation téléphone - DYNAMIQUE PAR PAYS
      const phoneDigits = extractPhoneDigits(formData.tel_client);
      const expectedLength = selectedCountry?.phoneLength;
      
      if (!/^\d+$/.test(phoneDigits)) {
        errors.push("Le numéro de téléphone doit contenir uniquement des chiffres");
      } else if (expectedLength && phoneDigits.length !== expectedLength) {
        errors.push(`Numéro ${selectedCountry?.name || ""} invalide (${phoneDigits.length} chiffres, besoin de ${expectedLength} chiffres)`);
      } else if (!expectedLength && (phoneDigits.length < 8 || phoneDigits.length > 15)) {
        errors.push(`Numéro invalide (${phoneDigits.length} chiffres, doit être entre 8 et 15 chiffres)`);
      }
    }
    
    if (formData.email_client && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_client)) {
      errors.push("L'email n'est pas valide");
    }
    
    if (!formData.date_debut_contrat_client) {
      errors.push("La date de début de contrat est requise");
    }
    
    if (!formData.date_fin_contrat_client) {
      errors.push("La date de fin de contrat est requise");
    }
    
    if (formData.date_debut_contrat_client && formData.date_fin_contrat_client) {
      const debut = new Date(formData.date_debut_contrat_client);
      const fin = new Date(formData.date_fin_contrat_client);
      
      if (debut >= fin) {
        errors.push("La date de fin doit être après la date de début");
      }
    }
    
    // Validation spécifique selon le type de client
    if (formData.type_client === "2") { // Particulier
      // Nom/prénom d'une personne physique : lettres uniquement (contrairement
      // au nom d'une entreprise, qui peut légitimement contenir des chiffres).
      const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
      if (formData.nom_client.trim() && !nameRegex.test(formData.nom_client.trim())) {
        errors.push("Le nom ne doit contenir que des lettres pour un particulier");
      }
      if (formData.prenom_client.trim() && !nameRegex.test(formData.prenom_client.trim())) {
        errors.push("Le prénom ne doit contenir que des lettres");
      }
      if (!formData.piece_identite_client.trim()) {
        errors.push("La pièce d'identité est requise pour un particulier");
      }
    } else if (formData.type_client === "1") { // Entreprise
      if (!formData.nif_client.trim()) {
        errors.push("Le NIF est requis pour une entreprise");
      }
      if (!formData.rccm_client.trim()) {
        errors.push("Le RCCM est requis pour une entreprise");
      }
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      
      // Préparer les données pour l'API (avec téléphone formaté)
      const updateData = {
        id_client: parseInt(id),
        nom_client: formData.nom_client,
        prenom_client: formData.prenom_client,
        // Formatage du téléphone avec indicatif
        tel_client: formatFullPhoneNumber(
          extractPhoneDigits(formData.tel_client), 
          selectedCountry?.code || "+227"
        ),
        email_client: formData.email_client,
        type_client: parseInt(formData.type_client),
        date_debut_contrat_client: formData.date_debut_contrat_client,
        date_fin_contrat_client: formData.date_fin_contrat_client,
        piece_identite_client: formData.piece_identite_client,
        nif_client: formData.nif_client,
        rccm_client: formData.rccm_client,
        statut_client: parseInt(formData.statut_client)
      };
      
      console.log("Données envoyées:", updateData);
      console.log("Téléphone formaté:", updateData.tel_client);
      
      const response = await axios.post(
        `${API_BASE_URL}/clients/update.php`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Client modifié avec succès !");
        
        // Redirection après 2 secondes
        setTimeout(() => {
          navigate(`/clients/details/${id}`);
        }, 2000);
      } else {
        setError(response.data?.message || "Erreur lors de la modification");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        // 🔍 Vérifier si c'est une erreur d'abonnement
        if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        }
      } else if (err.response?.status === 409) {
        setError("NIF ou RCCM déjà utilisé par un autre client.");
      } else if (err.response?.status === 400) {
        if (err.response.data?.errors) {
          setValidationErrors(err.response.data.errors);
        } else {
          setError(err.response.data?.message || "Données invalides");
        }
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser le formulaire
  const handleReset = () => {
    if (originalData) {
      // Détecter le pays à partir du numéro original
      let detectedCountry = countryCodes[0];
      if (originalData.tel_client) {
        detectedCountry = detectCountryCode(originalData.tel_client);
      }
      
      setSelectedCountry(detectedCountry);
      
      // Extraire les chiffres du numéro original
      const phoneDigits = extractPhoneDigits(originalData.tel_client || "");
      
      const formattedData = {
        nom_client: originalData.nom_client || "",
        prenom_client: originalData.prenom_client || "",
        tel_client: phoneDigits,
        email_client: originalData.email_client || "",
        type_client: String(originalData.type_client || "1"),
        date_debut_contrat_client: originalData.date_debut_contrat_client 
          ? originalData.date_debut_contrat_client.split(" ")[0]
          : "",
        date_fin_contrat_client: originalData.date_fin_contrat_client
          ? originalData.date_fin_contrat_client.split(" ")[0]
          : "",
        piece_identite_client: originalData.piece_identite_client || "",
        nif_client: originalData.nif_client || "",
        rccm_client: originalData.rccm_client || "",
        statut_client: String(originalData.statut_client || "1")
      };
      setFormData(formattedData);
    }
    setError("");
    setSuccessMsg("");
    setValidationErrors([]);
  };

  // Annuler et retourner
  const handleCancel = () => {
    navigate(`/clients/details/${id}`);
  };

  // Calculer les jours restants
  const calculateDaysRemaining = () => {
    if (!formData.date_fin_contrat_client) return 0;
    const today = new Date();
    const endDate = new Date(formData.date_fin_contrat_client);
    const diffTime = endDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Calculer la durée du contrat
  const calculateContractDuration = () => {
    if (!formData.date_debut_contrat_client || !formData.date_fin_contrat_client) return 0;
    const startDate = new Date(formData.date_debut_contrat_client);
    const endDate = new Date(formData.date_fin_contrat_client);
    const diffTime = endDate - startDate;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Afficher un message si l'utilisateur n'est pas gestionnaire
  if (!loading && userInfo && String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le client</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/clients">Clients</Link>
              </li>
              <li className="breadcrumb-item active">Modifier</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
              <p style={{ color: COLORS.darkText }}>Seuls les gestionnaires peuvent modifier les clients.</p>
              <button
                className="btn fw-semibold mt-3"
                onClick={() => navigate("/clients")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le client</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/clients">Clients</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des données...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error && !loading && !originalData) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le client</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/clients">Clients</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.danger }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="alert alert-danger alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
              <div className="text-center mt-3">
                <button 
                  className="btn fw-semibold"
                  onClick={() => navigate("/clients")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la liste
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le client</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/clients">Clients</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/clients/details/${id}`} style={{ color: COLORS.secondary }}>
                {originalData?.nom_client} {originalData?.prenom_client}
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-pencil-square me-2" style={{ color: COLORS.secondary }}></i>
              Formulaire de modification du client
            </h5>

            {/* Messages */}
            {successMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <div className="flex-grow-1">
                    {successMsg}
                    <div className="mt-2">
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#2e7d32' }}></div>
                      Redirection en cours...
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccessMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff8e1',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.warning
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    <strong>Veuillez corriger les erreurs suivantes :</strong>
                    <ul className="mb-0 mt-2">
                      {validationErrors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Informations personnelles */}
                <div className="col-md-6">
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-person-vcard"></i>
                        </div>
                        Informations personnelles
                      </h6>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-person me-1" style={{ color: COLORS.secondary }}></i>
                          Nom <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person-vcard"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="nom_client"
                            value={formData.nom_client}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-person-bounding-box me-1" style={{ color: COLORS.secondary }}></i>
                          Prénom
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="prenom_client"
                            value={formData.prenom_client}
                            onChange={handleChange}
                            disabled={saving}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-phone me-1" style={{ color: COLORS.secondary }}></i>
                          Téléphone <span className="text-danger">*</span>
                        </label>
                        
                        <div className="d-flex align-items-stretch" style={{ gap: '8px' }}>
                          <CountrySelector
                            showList={showCountryList}
                            setShowList={setShowCountryList}
                            selectedCountry={selectedCountry}
                            setSelectedCountry={setSelectedCountry}
                            search={searchCountry}
                            setSearch={setSearchCountry}
                            listRef={countryListRef}
                            disabled={saving}
                          />
                          
                          <div className="position-relative" style={{ flex: '1' }}>
                            <div style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#666',
                              fontSize: '0.95rem',
                              pointerEvents: 'none',
                              zIndex: 5
                            }}>
                              {selectedCountry?.code || "+227"}
                            </div>
                            <input
                              type="tel"
                              id="tel_client"
                              name="tel_client"
                              className="form-control"
                              value={formData.tel_client}
                              onChange={handleChange}
                              placeholder={selectedCountry?.phoneLength ? 
                                `Ex: ${generatePhoneExample(selectedCountry.phoneLength)}` : 
                                "Numéro de téléphone"}
                              required
                              disabled={saving}
                              maxLength={selectedCountry?.phoneLength || 15}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              style={{ 
                                borderRadius: '8px',
                                borderColor: '#e0e0e0',
                                padding: '12px 16px 12px 55px',
                                fontSize: '0.95rem',
                                width: '100%',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                        </div>
                        
                        <small className="text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                          Format: {selectedCountry?.code || "+227"} + {selectedCountry?.phoneLength || "?"} chiffres
                          {selectedCountry?.phoneLength && 
                            ` (ex: ${selectedCountry.code}${generatePhoneExample(selectedCountry.phoneLength)})`}
                        </small>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-envelope me-1" style={{ color: COLORS.secondary }}></i>
                          Email
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-at"></i>
                          </span>
                          <input
                            type="email"
                            className="form-control"
                            name="email_client"
                            value={formData.email_client}
                            onChange={handleChange}
                            disabled={saving}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-person-badge me-1" style={{ color: COLORS.secondary }}></i>
                          Type de client <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-people"></i>
                          </span>
                          <select
                            className="form-select"
                            name="type_client"
                            value={formData.type_client}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="1">Entreprise</option>
                            <option value="2">Particulier</option>
                          </select>
                        </div>
                        <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          {formData.type_client === "1" 
                            ? "Entreprise : NIF et RCCM obligatoires" 
                            : "Particulier : Pièce d'identité obligatoire"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations légales et statut */}
                <div className="col-md-6">
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-file-text"></i>
                        </div>
                        Informations légales et statut
                      </h6>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-credit-card me-1" style={{ color: COLORS.secondary }}></i>
                          Pièce d'identité
                          {formData.type_client === "2" && <span className="text-danger">*</span>}
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-card-text"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="piece_identite_client"
                            value={formData.piece_identite_client}
                            onChange={handleChange}
                            required={formData.type_client === "2"}
                            disabled={saving}
                            placeholder="Carte nationale, Passeport..."
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-file-text me-1" style={{ color: COLORS.secondary }}></i>
                          NIF
                          {formData.type_client === "1" && <span className="text-danger">*</span>}
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-file-earmark-text"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="nif_client"
                            value={formData.nif_client}
                            onChange={handleChange}
                            required={formData.type_client === "1"}
                            disabled={saving}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-file-text me-1" style={{ color: COLORS.secondary }}></i>
                          RCCM
                          {formData.type_client === "1" && <span className="text-danger">*</span>}
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-file-earmark-text"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="rccm_client"
                            value={formData.rccm_client}
                            onChange={handleChange}
                            required={formData.type_client === "1"}
                            disabled={saving}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-check-circle me-1" style={{ color: COLORS.secondary }}></i>
                          Statut <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-activity"></i>
                          </span>
                          <select
                            className="form-select"
                            name="statut_client"
                            value={formData.statut_client}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            style={{
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="1">Actif</option>
                            <option value="0">Inactif</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates du contrat */}
                <div className="col-12">
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-calendar-check"></i>
                        </div>
                        Informations du contrat
                      </h6>
                      
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              <i className="bi bi-calendar-plus me-1" style={{ color: COLORS.secondary }}></i>
                              Date de début <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-calendar-date"></i>
                              </span>
                              <input
                                type="date"
                                className="form-control"
                                name="date_debut_contrat_client"
                                value={formData.date_debut_contrat_client}
                                onChange={handleChange}
                                required
                                disabled={saving}
                                style={{
                                  borderColor: '#e0e0e0',
                                  borderRadius: '0 8px 8px 0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              <i className="bi bi-calendar-minus me-1" style={{ color: COLORS.secondary }}></i>
                              Date de fin <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-calendar-x"></i>
                              </span>
                              <input
                                type="date"
                                className="form-control"
                                name="date_fin_contrat_client"
                                value={formData.date_fin_contrat_client}
                                onChange={handleChange}
                                required
                                disabled={saving}
                                style={{
                                  borderColor: '#e0e0e0',
                                  borderRadius: '0 8px 8px 0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Calcul des jours restants */}
                      {formData.date_debut_contrat_client && formData.date_fin_contrat_client && (
                        <div className="alert mt-3" style={{ 
                          backgroundColor: '#E3F2FD',
                          borderColor: COLORS.accent,
                          color: COLORS.primary,
                          borderRadius: '8px'
                        }}>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32'
                                }}>
                                  <i className="bi bi-clock-history"></i>
                                </div>
                                <div>
                                  <div style={{ fontWeight: '500', color: COLORS.darkText }}>Durée du contrat</div>
                                  <div style={{ color: COLORS.darkText, fontWeight: '600' }}>
                                    {calculateContractDuration()} jour(s)
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: calculateDaysRemaining() > 30 ? '#e8f5e9' : 
                                                calculateDaysRemaining() > 0 ? '#fff8e1' : '#ffebee',
                                  color: calculateDaysRemaining() > 30 ? '#2e7d32' : 
                                         calculateDaysRemaining() > 0 ? '#ff8f00' : '#c62828'
                                }}>
                                  <i className="bi bi-alarm"></i>
                                </div>
                                <div>
                                  <div style={{ fontWeight: '500', color: COLORS.darkText }}>Jours restants</div>
                                  <div style={{ 
                                    color: calculateDaysRemaining() > 30 ? '#2e7d32' : 
                                           calculateDaysRemaining() > 0 ? '#ff8f00' : '#c62828',
                                    fontWeight: '600'
                                  }}>
                                    {calculateDaysRemaining()} jour(s)
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ 
                borderColor: COLORS.lightBg
              }}>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={saving}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleReset}
                    disabled={saving}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="btn fw-semibold"
                  disabled={saving}
                  style={{
                    color: 'white',
                    backgroundColor: saving ? '#B0BEC5' : COLORS.primary,
                    borderColor: saving ? '#B0BEC5' : COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    opacity: saving ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Enregistrer les modifications
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          background-color: ${COLORS.lightBg};
          border-color: #dee2e6;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-md-6 {
            margin-bottom: 1.5rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ClientEdit;